/**
 * Material types utility functions
 * Handles the new compliant material types
 */

/**
 * Get display information for a material type
 * @param {string} type - Material type
 * @returns {Object} - Display info with label, color, icon, etc.
 */
export const getMaterialTypeInfo = (type) => {
  const materialTypes = {
    study_notes: {
      label: 'Study Notes',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      icon: 'notes',
      description: 'Personal notes and summaries created by students'
    },
    study_guide: {
      label: 'Study Guide',
      color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      icon: 'guide',
      description: 'Student-created guides for exam preparation'
    },
    practice_materials: {
      label: 'Practice Materials',
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      icon: 'practice',
      description: 'Student-generated practice problems and exercises'
    },
    course_resources: {
      label: 'Course Resources',
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      icon: 'resources',
      description: 'Educational resources and supplementary materials'
    },
    group_study: {
      label: 'Group Study',
      color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      icon: 'group',
      description: 'Collaborative study materials created by student groups'
    }
  };

  return materialTypes[type] || {
    label: type.charAt(0).toUpperCase() + type.slice(1),
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    icon: 'unknown',
    description: 'Unknown material type'
  };
};

/**
 * Get material type icon component
 * @param {string} type - Material type
 * @param {string} className - CSS classes for the icon
 * @returns {JSX.Element} - Icon component
 */
export const getMaterialTypeIcon = (type, className = "w-4 h-4") => {
  const iconMap = {
    // New types
    study_notes: (
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
        <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v6.5a.5.5 0 01-.5.5h-11A.5.5 0 014 11.5V5zm3 2.5a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5a.5.5 0 01-.5-.5zm0 2a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5a.5.5 0 01-.5-.5z" clipRule="evenodd" />
      </svg>
    ),
    study_guide: (
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
      </svg>
    ),
    practice_materials: (
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0v3a1 1 0 102 0v-3zm2-3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4-1a1 1 0 10-2 0v7a1 1 0 102 0V8z" clipRule="evenodd" />
      </svg>
    ),
    course_resources: (
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
      </svg>
    ),
    group_study: (
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
      </svg>
    )
  };
  
  return iconMap[type] || (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm3 5a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
    </svg>
  );
};

/**
 * Get material type options for dropdowns and filters
 * @param {boolean} includeAll - Include "All" option
 * @returns {Array} - Array of type options
 */
export const getMaterialTypeOptions = (includeAll = true) => {
  const options = [];
  
  if (includeAll) {
    options.push({ value: '', label: 'All Types' });
  }
  
  options.push(
    { value: 'study_notes', label: 'Study Notes' },
    { value: 'study_guide', label: 'Study Guide' },
    { value: 'practice_materials', label: 'Practice Materials' },
    { value: 'course_resources', label: 'Course Resources' },
    { value: 'group_study', label: 'Group Study' }
  );
  
  return options;
};